/* aminos-nutrition-support-buybox — page-scoped behaviour layered on top of
 * assets/aminos-featured-product.js, which is shared with ~15 other pages
 * and is deliberately left untouched.
 *
 * Sole job: keep each option card's quantity-dependent copy in step with
 * the selector.
 *
 *   - Pack count: choosing quantity 2 turns "28 packs, every 4 weeks" into
 *     "56 packs, every 4 weeks". The forked snippet renders the number
 *     into its own [data-ns-packs] span and stashes the per-unit count
 *     on data-ns-pack-base, so this file only ever multiplies that
 *     base — it never parses or rewrites the merchant's copy around it.
 *
 *   - Per-day qualifier: the per-day figure is deliberately NOT scaled
 *     (it is one person's daily cost, so two units is still $1.28 each,
 *     not $2.56 — the shared script makes the same call). Since the pack
 *     count beside it does scale, the "| Per Person" span is revealed once
 *     more than one unit is selected so the two can't be misread against
 *     the doubled total.
 *
 *   - Multi-adult bonus: when the form carries data-ns-bundle-min and
 *     the quantity reaches it, the subscription price, per-day figure,
 *     banner, offer title and discount note switch to their bonus versions
 *     (all rendered by the snippet as data-ns-base / data-ns-bundle, so
 *     this file never formats money or copy itself). The price works by
 *     swapping the element's fpBase, the unit price the shared script
 *     multiplies by quantity, so the shared script scales the right figure
 *     whichever of the two handlers runs last. The checkout script picks the
 *     matching discount code.
 *
 *   - 3-month card (Oct 2026): while its radio ([data-ns-three-month]) is
 *     picked, the form gets .is-three-month, which the CSS uses to hide the
 *     2-adult bonus hint and tag (the bonus is monthly only; 2 adults on the
 *     3-month plan is just 6 bags at the same 40%), and the discount note
 *     shows its data-ns-q-note text. The checkout script sends 3 bags per
 *     adult with no code.
 *
 * Loaded only by sections/aminos-nutrition-support-buybox.liquid.
 */
(function (global) {
  'use strict';

  if (global.__aminosNutritionSupportBuyboxInit) return;
  global.__aminosNutritionSupportBuyboxInit = true;

  var SCOPE = '.aminos-nutrition-support-buybox';

  function quantityOf(form) {
    var field =
      form.querySelector('input[name="quantity"]:checked') ||
      form.querySelector('input[name="quantity"]');
    return Math.max(1, parseInt((field && field.value) || '1', 10) || 1);
  }

  // Same maths as scaleMoney in the shared aminos-featured-product.js:
  // multiply the first number in a money string, keeping symbol and decimals.
  function scaleMoney(text, qty) {
    return text.replace(/[\d.,]+/, function (token) {
      var dot = token.lastIndexOf('.');
      var decimals = dot === -1 ? 0 : token.length - dot - 1;
      var value = parseFloat(token.replace(/,/g, ''));
      if (isNaN(value)) return token;
      return (value * qty).toLocaleString('en-US', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      });
    });
  }

  function refreshBundle(form, qty) {
    var min = parseInt(form.dataset.nsBundleMin, 10);
    if (!min) return;
    var bundle = qty >= min;

    form.querySelectorAll('[data-ns-bundle-price]').forEach(function (el) {
      var unit = bundle ? el.dataset.nsBundlePrice : el.dataset.nsBasePrice;
      el.dataset.fpBase = unit;
      el.textContent = scaleMoney(unit, qty);
    });

    form.querySelectorAll('[data-ns-swap]').forEach(function (el) {
      el.textContent = bundle ? el.dataset.nsBundle : el.dataset.nsBase;
    });
  }

  function refreshThreeMonth(form) {
    var picked = form.querySelector('[data-aminos-fp-radio]:checked');
    var three = !!(picked && picked.hasAttribute('data-ns-three-month'));
    form.classList.toggle('is-three-month', three);
    form.querySelectorAll('[data-ns-q-note]').forEach(function (el) {
      // refreshBundle has just written the 1-month text into a bonus-aware
      // note; a plain note needs its markup text put back by hand.
      if (el.dataset.nsQBase === undefined) el.dataset.nsQBase = el.textContent;
      if (three) el.textContent = el.dataset.nsQNote;
      else if (!el.hasAttribute('data-ns-swap')) el.textContent = el.dataset.nsQBase;
    });
  }

  function refreshQuantityCopy(form) {
    var qty = quantityOf(form);
    refreshBundle(form, qty);
    refreshThreeMonth(form);

    form.querySelectorAll('[data-ns-packs]').forEach(function (el) {
      var base = parseInt(el.getAttribute('data-ns-pack-base'), 10);
      if (!base) return;
      el.textContent = base * qty;
    });

    form.querySelectorAll('.aminos-nutrition-support-buybox__per-unit').forEach(function (el) {
      el.hidden = qty < 2;
    });
  }

  function refreshAll() {
    document.querySelectorAll(SCOPE + ' [data-aminos-fp-form]').forEach(refreshQuantityCopy);
  }

  // Delegated, matching how the shared script binds — survives the theme
  // editor swapping section markup out from under us.
  document.addEventListener('change', function (event) {
    var target = event.target;
    if (!target || !target.closest) return;
    // Quantity pills and purchase-option radios: either can change the copy.
    var radio = target.closest('[data-aminos-fp-qty], [data-aminos-fp-radio]');
    if (!radio) return;
    var form = radio.closest('[data-aminos-fp-form]');
    if (form && form.closest(SCOPE)) refreshQuantityCopy(form);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', refreshAll);
  } else {
    refreshAll();
  }

  document.addEventListener('shopify:section:load', refreshAll);
})(window);
